import React, { useState, useEffect, useRef } from 'react';
import { 
  getDocuments, 
  getDocumentTypes, 
  createDocument, 
  updateDocument, 
  deleteDocument,
  uploadFile,
  getEntityFiles,
  deleteFile,
  API_BASE,
  getAuthHeaders
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Plus, 
  Upload, 
  Download, 
  Trash2, 
  Edit, 
  MoreHorizontal, 
  FileText, 
  File,
  Eye,
  Save,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

const getExpiryBadge = (daysUntil) => {
  if (daysUntil === null || daysUntil === undefined) return null;
  if (daysUntil < 0) return <Badge variant="destructive">Expired</Badge>;
  if (daysUntil <= 7) return <Badge className="bg-red-500">Expires in {daysUntil}d</Badge>;
  if (daysUntil <= 30) return <Badge className="bg-orange-500">Expires in {daysUntil}d</Badge>;
  if (daysUntil <= 90) return <Badge className="bg-yellow-500">Expires in {daysUntil}d</Badge>;
  return <Badge variant="secondary">{daysUntil}d remaining</Badge>;
};

export default function DocumentsTab({ entityType, entityId, entityName }) {
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    doc_type_id: '',
    document_number: '',
    issue_date: '',
    expiry_date: '',
    issuing_authority: '',
    notes: ''
  });

  useEffect(() => {
    if (entityId) {
      loadData();
    }
  }, [entityId]);

  const loadData = async () => {
    try {
      const [docsRes, typesRes] = await Promise.all([
        getDocuments({ 
          [`${entityType.toLowerCase()}_owner_id`]: entityId,
          owner_type: entityType,
          limit: 100 
        }),
        getDocumentTypes()
      ]);
      
      setDocuments(docsRes.data.items || []);
      setDocumentTypes(typesRes.data || []);
      
      // Load attached files for each document
      const filesMap = {};
      for (const doc of (docsRes.data.items || [])) {
        try {
          const filesRes = await getEntityFiles('Document', doc.id);
          filesMap[doc.id] = filesRes.data || [];
        } catch (e) {
          filesMap[doc.id] = [];
        }
      }
      setAttachedFiles(filesMap);
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setFormData({
        doc_type_id: doc.doc_type_id || '',
        document_number: doc.document_number || '',
        issue_date: doc.issue_date?.split('T')[0] || '',
        expiry_date: doc.expiry_date?.split('T')[0] || '',
        issuing_authority: doc.issuing_authority || '',
        notes: doc.notes || ''
      });
    } else {
      setEditingDoc(null);
      setFormData({
        doc_type_id: '',
        document_number: '',
        issue_date: '',
        expiry_date: '',
        issuing_authority: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.doc_type_id) {
      toast.error('Please select document type');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        owner_type: entityType,
        [`${entityType.toLowerCase()}_owner_id`]: entityId
      };

      if (editingDoc) {
        await updateDocument(editingDoc.id, payload);
        toast.success('Document updated');
      } else {
        await createDocument(payload);
        toast.success('Document added');
      }
      
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document record?')) return;
    
    try {
      await deleteDocument(docId);
      toast.success('Document deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleFileUpload = async (docId, file) => {
    if (!file) return;
    
    setUploadingFile(docId);
    try {
      await uploadFile(file, 'Document', docId);
      toast.success('File uploaded');
      loadData();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(null);
    }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    
    try {
      await deleteFile(fileId);
      toast.success('File deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleDownload = (fileId, filename) => {
    const url = `${API_BASE}/files/${fileId}`;
    
    // Use credentials: 'include' for cookie + Authorization header as fallback
    fetch(url, {
      credentials: 'include',
      headers: getAuthHeaders()
    })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error('Failed to download file'));
  };

  const getDocTypeName = (docTypeId) => {
    const type = documentTypes.find(t => t.id === docTypeId);
    return type?.name || 'Unknown';
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8">Loading documents...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Documents for {entityName}</h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} data-testid="add-document-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingDoc ? 'Edit Document' : 'Add Document'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Document Type *</Label>
                <Select 
                  value={formData.doc_type_id} 
                  onValueChange={(v) => setFormData(p => ({ ...p, doc_type_id: v }))}
                >
                  <SelectTrigger data-testid="doc-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Document Number</Label>
                <Input
                  value={formData.document_number}
                  onChange={(e) => setFormData(p => ({ ...p, document_number: e.target.value }))}
                  placeholder="e.g., P1234567"
                  data-testid="doc-number-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Issue Date</Label>
                  <Input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData(p => ({ ...p, issue_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData(p => ({ ...p, expiry_date: e.target.value }))}
                    data-testid="doc-expiry-input"
                  />
                </div>
              </div>

              <div>
                <Label>Issuing Authority</Label>
                <Input
                  value={formData.issuing_authority}
                  onChange={(e) => setFormData(p => ({ ...p, issuing_authority: e.target.value }))}
                  placeholder="e.g., Ministry of Interior"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-document-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {documents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>No documents recorded yet</p>
            <p className="text-sm">Click "Add Document" to add passport, visa, or other documents</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {documents.map((doc) => (
            <Card key={doc.id} data-testid={`document-card-${doc.id}`}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold">{getDocTypeName(doc.doc_type_id)}</h4>
                      {getExpiryBadge(doc.days_until_expiry)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Number:</span>
                        <p className="font-mono">{doc.document_number || '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Issue Date:</span>
                        <p>{doc.issue_date ? formatDate(doc.issue_date) : '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Expiry Date:</span>
                        <p>{doc.expiry_date ? formatDate(doc.expiry_date) : '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Authority:</span>
                        <p>{doc.issuing_authority || '-'}</p>
                      </div>
                    </div>

                    {/* Attached Files */}
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Attached Files</span>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => handleFileUpload(doc.id, e.target.files[0])}
                            disabled={uploadingFile === doc.id}
                          />
                          <Button 
                            variant="outline" 
                            size="sm" 
                            asChild
                            disabled={uploadingFile === doc.id}
                          >
                            <span>
                              <Upload className="w-3 h-3 mr-1" />
                              {uploadingFile === doc.id ? 'Uploading...' : 'Upload'}
                            </span>
                          </Button>
                        </label>
                      </div>
                      
                      {(attachedFiles[doc.id] || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No files attached</p>
                      ) : (
                        <div className="space-y-1">
                          {(attachedFiles[doc.id] || []).map((file) => (
                            <div 
                              key={file.file_id} 
                              className="flex items-center justify-between p-2 bg-secondary/50 rounded text-sm"
                            >
                              <div className="flex items-center gap-2">
                                <File className="w-4 h-4 text-muted-foreground" />
                                <span className="truncate max-w-[200px]">{file.filename}</span>
                                <span className="text-muted-foreground text-xs">
                                  ({(file.size / 1024).toFixed(1)} KB)
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleDownload(file.file_id, file.filename)}
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-500 hover:text-red-600"
                                  onClick={() => handleFileDelete(file.file_id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleOpenDialog(doc)}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDelete(doc.id)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
